import React, { useEffect, useState } from "react";
import { deepmerge } from "deepmerge-ts";
import parseToml from "@iarna/toml/parse-string";
import { unreachable } from "@opencast/appkit";

import { isNullUndefinedOrEmpty, usePresentContext, onMobileDevice, getWebGLRendererInfo } from "./util";
import { DEFINES } from "./defines";
import { initializePostHog } from "./tracking/posthog";

import
{ presentationStates,
  getUpidFromToken, LOCAL_STORAGE_SITE_ID, LOCAL_STORAGE_SITE_NAME
} from "./mediasite";
import type { VideoServerInterface, VSIReturn } from "./video-server/video-server-interface";
import MvpVideoServer from "./video-server/mvp-video-server";
import MomVideoServer from "./video-server/mom-video-server";

const LOCAL_STORAGE_KEY = "MediaiteWebRecorderSettings";
const CONTEXT_SETTINGS_FILE = "settings.toml";
const QUERY_PARAM_SETTINGS_PATH = "settingsFile";

export const FORM_FIELD_HIDDEN = "hidden";
export const FORM_FIELD_OPTIONAL = "optional";
export const FORM_FIELD_REQUIRED = "required";
export type ServerType = "MVP" | "MOM";
export type FormFieldState =
  | typeof FORM_FIELD_HIDDEN
  | typeof FORM_FIELD_OPTIONAL
  | typeof FORM_FIELD_REQUIRED;

/** Sources that setting values can come from. */
type SettingsSource = "src-server"| "src-url" | "src-local-storage";

const PRESENTER_SOURCES = ["opencast"] as const;
type PresenterSource = typeof PRESENTER_SOURCES[number];

/** Map from roles to array of permitted actions */
export type Acl = Map<string, string[]>;

export const DEFAULT_ACL: Acl = new Map([
  ["{{ user.userRole }}", ["read", "write"]],
]);

/** Opencast Studio runtime settings. */
export type Settings = {
  opencast?: {
    serverUrl?: string; // TODO: make this URL
    serverType?: ServerType;
    loginName?: string;
    loginPassword?: string;
    loginProvided?: boolean;
    sfApiKey?: string;
    launchToken?: string;
    sessionToken?: string;
  };
  upload?: {
    seriesId?: string;
    workflowId?: string;
    acl?: boolean | string | Acl;
    dcc?: string;
    titleField?: FormFieldState;
    presenterField?: FormFieldState;
    seriesField?: FormFieldState;
    autofillPresenter?: PresenterSource[];
    presentationId?: string;
    uploadUrl?: string;
    createMediaUploadUrl?: string;
    createNewFileDeletionRecords?: string;
    uploadWhileRecording?: boolean;
    presentationUrl?: string;
  };
  recording?: {
    videoBitrate?: number;
    mimes?: string[];
    skipStopConfirmation?: boolean;
  };
  review?: {
    disableCutting?: boolean;
  };
  teleprompter?: {
    /** Default true (absence means "keep") — see `teleprompter-prefs.ts`'s `clearTeleprompterScript`. */
    keepScriptAfterPublish?: boolean;
  };
  display?: {
    maxFps?: number;
    maxHeight?: number;
  };
  camera?: {
    maxFps?: number;
    maxHeight?: number;
  };
  performance?: {
    /**
     * 'auto' = use heuristic
     * 'low' = force low-power
     * 'medium' = target mid
     * 'high' = prefer high quality
     * 'maximum' = uncapped native device resolution
     */
    quality?: "auto" | "low" | "medium" | "high" | "maximum";
  };
  return?: {
    allowedDomains?: string[];
    label?: string;
    target?: string;
  };
  walkMeOptOut?: boolean;
  fullStoryOptOut?: boolean;
};

export interface MSErrorConditions {
  isMediasiteDataPresent: boolean;
  isMediasiteCommunicationSuccessful: boolean;
  errorMessage: string;
  isTranslatable: boolean;
}

/**
 * The values prefilled on the settings page. These settings are *not* used
 * automatically, they are just the defaults for the UI.
 */
const defaultSettings: Settings = {
  opencast: {
    serverUrl: "https://develop.opencast.org/",
    serverType: "MVP",
    loginName: "admin",
    loginPassword: "opencast",
    sfApiKey: "446b90be-10b8-4d35-b57a-6d68239a888d",
    launchToken: "",
    sessionToken: "",
  },
  upload: {
    presentationId: "",
    uploadUrl: "",
    createMediaUploadUrl: "",
    createNewFileDeletionRecords: "",
    uploadWhileRecording: true,
    presentationUrl: "",
  },
  performance: {
    quality: "auto",
  },
};


// ==============================================================================================
// ===== SettingsManager
// ==============================================================================================

/**
 * Responsible for obtaining settings from different places (context settings,
 * local storage, query parameter) and merging them appropriately.
 */
export class SettingsManager {
  /**
   * The settings set by the server. These cannot be edited by the user. If the
   * server did not specify any settings, this is `{}`.
   */
  contextSettings: Settings = Object.create(null);

  /**
   * These settings are given in the query part of the URL (e.g.
   * `?opencast.loginName=peter`). If there are no settings in the URL, this
   * is `{}`.
   */
  urlSettings: Settings = Object.create(null);

  /**
   * The settings set by the user and stored in local storage. This is `{}` if
   * there were no settings in local storage.
   */
  #userSettings: Settings = Object.create(null);

  // The settings for Mediasite server
  opencast = defaultSettings.opencast;

  // The settings for the individual presentation
  upload = defaultSettings.upload;

  errorConditions: MSErrorConditions = {
    // Did we get all the information necessary to run Capture?
    isMediasiteDataPresent: true,

    // Did we error on initial communication with MVP?
    isMediasiteCommunicationSuccessful: true,

    errorMessage: "",
    isTranslatable: false,
  };

  /**
   * This function is called whenever the user saved their settings. The new
   * settings object is passed as parameter.
   */
  onChange: (newSettings: Settings) => void = () => {
    // By default, this does nothing. It is overwritten below.
  };

  static getDomain(url: string | null | undefined) {
    if (isNullUndefinedOrEmpty(url)) {
      return null;
    }

    try {
      const urlObject = new URL(url!);
      return urlObject.hostname.replace("www.", "");
    } catch {
      return null;
    }
  }

  /**
   * Creates a new `Settings` instance by loading user settings from local
   * storage and attempting to load context settings from the server..
   */
  static async init() {
    const self = new SettingsManager();

    // Load the user settings from local storage
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored !== null) {
      let rawUserSettings;
      try {
        rawUserSettings = JSON.parse(stored);
      } catch {
        console.warn("Could not parse settings stored in local storage. Ignoring.");
      }
      self.#userSettings = validate(
        rawUserSettings,
        false,
        "src-local-storage",
        "from local storage user settings",
      );
    }
    // If the user did not explicitly set a quality preference, or if they
    // selected `auto`, detect a sensible default for this session based on
    // device heuristics. We treat an explicit `auto` as "let the app decide".
    // This detection stays session-only (not persisted) unless the user
    // chooses to remember their selection in the UI.
    const userQuality = self.#userSettings?.performance?.quality;
    if (userQuality === undefined || userQuality === "auto") {
      const detected = detectQualityByDevice();
      self.#userSettings = merge(self.#userSettings, { performance: { quality: detected } });
      console.debug(`[settings] auto-detected performance.quality=${detected} (userQuality=${userQuality})`);
    }

    // Get settings from URL query. We remove the key `settingsFile` as that is
    // just used for loading the context settings.
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(QUERY_PARAM_SETTINGS_PATH);

    let videoServer: VideoServerInterface | null = null;
    const serverTypeParam = urlParams.get("ServerType");
    const hostname = window.location.hostname.toLowerCase();
    const isMvpDomain = hostname === "mediasitecapture.com" || hostname.endsWith(".mediasitecapture.com");
    const isMomDomain = hostname === "momindum.com" || hostname.endsWith(".momindum.com");
    const inferredServerType: ServerType = isMvpDomain ? "MVP" : (isMomDomain ? "MOM" : "MVP");
    const serverType = serverTypeParam ?? inferredServerType;
    const resolvedServerType: ServerType = serverType === "MOM" || serverType === "MVP" ? serverType : "MVP";

    if (self.opencast !== undefined) {
      self.opencast.serverType = resolvedServerType;
    }

    if (serverType === "MVP") {
      videoServer = new MvpVideoServer();
    } else if (serverType === "MOM") {
      videoServer = new MomVideoServer();
    } else {
      self.errorConditions.isMediasiteDataPresent = false;
      return [self, null] as [SettingsManager, VideoServerInterface | null];
    }

    if (self.opencast !== undefined) {
      const siteUrlParm = urlParams.get("SiteURL");

      if (siteUrlParm !== null && siteUrlParm !== "") {
        self.opencast.serverUrl = siteUrlParm;
      }

      const launchTokenParm = urlParams.get("Token");

      if (launchTokenParm !== null && launchTokenParm !== "") {
        self.opencast.launchToken = launchTokenParm;
        let loginNameData: string | null = "OtherVideoServer";

        if (serverType === "MVP") {
          loginNameData = getUpidFromToken(self.opencast.launchToken);
        }

        if (loginNameData !== null) {
          self.opencast.loginName = loginNameData;
        }
      }
    }

    if (self.upload !== undefined) {
      const presentationIdParm = urlParams.get("PresentationId");

      if (presentationIdParm !== null && presentationIdParm !== "") {
        self.upload.presentationId = presentationIdParm;
      }
    }

    if (self.opencast === undefined || self.upload === undefined ||
      isNullUndefinedOrEmpty(self.opencast.serverUrl) ||
      isNullUndefinedOrEmpty(self.opencast.launchToken) ||
      isNullUndefinedOrEmpty(self.opencast.loginName) ||
      isNullUndefinedOrEmpty(self.upload.presentationId) ||
      isNullUndefinedOrEmpty(self.opencast.sfApiKey)) {
      self.errorConditions.isMediasiteDataPresent = false;
      return [self, null] as [SettingsManager, VideoServerInterface | null];
    }

    const ret: VSIReturn = await videoServer.initialize(self.opencast.serverUrl!, self.opencast.sfApiKey!,
      self.opencast.launchToken!, self.upload.presentationId!);

    if (ret.successful === false) {
      console.error(ret.errorMessage);
      self.errorConditions.isMediasiteCommunicationSuccessful = false;
      return [self, videoServer] as [SettingsManager, VideoServerInterface | null];
    }

    const walkMeOptOut = urlParams.get("walkMeOptOut");
    const fullStoryOptOut = urlParams.get("fullStoryOptOut");
    self.contextSettings.walkMeOptOut = walkMeOptOut === null ? false : walkMeOptOut.toLowerCase() === "true";
    self.contextSettings.fullStoryOptOut =
      fullStoryOptOut === null ? false : fullStoryOptOut.toLowerCase() === "true";

    if (self.contextSettings.walkMeOptOut === false &&
      self.contextSettings.fullStoryOptOut === false) {
      const visitorId = self.opencast.loginName;
      const captureVersion = DEFINES.commitSha === null ? "" : DEFINES.commitSha;
      const accountId = this.getDomain(self.opencast.serverUrl);
      const siteId = window.localStorage.getItem(LOCAL_STORAGE_SITE_ID);
      const siteName = window.localStorage.getItem(LOCAL_STORAGE_SITE_NAME);
      initializePostHog(visitorId!, captureVersion, accountId!, siteId === null ? "" : siteId,
        siteName === null ? "" : siteName, resolvedServerType, hostname);
    }

    videoServer.changePresentationState(presentationStates.LAUNCHED);

    if (self.#userSettings?.upload?.uploadWhileRecording !== undefined) {
      self.upload.uploadWhileRecording = self.#userSettings.upload.uploadWhileRecording;
    }

    // Listen for settings changes from system settings popup
    window.addEventListener("mediasite-capture:settings-changed", ((event: CustomEvent) => {
      const { key } = event.detail;

      // Reload settings from localStorage
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored !== null) {
        let rawUserSettings;
        try {
          rawUserSettings = JSON.parse(stored);
          self.#userSettings = validate(
            rawUserSettings,
            false,
            "src-local-storage",
            "from local storage user settings",
          );
        } catch {
          console.warn("Could not parse settings stored in local storage. Ignoring.");
        }
      }

      // Update the specific setting
      if (key === "upload.uploadWhileRecording" && self.#userSettings?.upload?.uploadWhileRecording !== undefined) {
        self.upload!.uploadWhileRecording = self.#userSettings.upload.uploadWhileRecording;
      }

      // Notify listeners of the change
      self.onChange(self.settings());
    }) as EventListener);

    return [self, videoServer] as [SettingsManager, VideoServerInterface];
  }

  /**
   * Attempts to load `settings.toml` (or SETTINGS_PATH if that's specified)
   * from the server. If it fails for some reason, returns `null` and prints an
   * appropriate message on console.
   */
  static async loadContextSettings() {
    // Construct path to settings file. If the `SETTINGS_PATH` is given and
    // starts with '/', it is interpreted as absolute path from the server
    // root.
    let settingsPath = DEFINES.settingsPath || CONTEXT_SETTINGS_FILE;

    // If a custom file is given via query parameter, change the settings path
    // appropriately.
    const urlParams = new URLSearchParams(window.location.search);
    const customFile = urlParams.get(QUERY_PARAM_SETTINGS_PATH);
    if (customFile) {
      if (customFile.includes("/") || customFile.includes("\\")) {
        console.warn(`You can only specify a filename via '${QUERY_PARAM_SETTINGS_PATH}', `
          + "not a path");
      } else {
        const segments = settingsPath.split("/");
        segments[segments.length - 1] = customFile;
        settingsPath = segments.join("/");
      }
    }

    const base = settingsPath.startsWith("/") ? "" : DEFINES.publicPath;
    const url = `${window.location.origin}${base}${settingsPath}`;
    let response: Response;
    try {
      response = await fetch(url);
    } catch (e) {
      console.warn(`Could not access '${settingsPath}' due to network error!`, e || "");
      return null;
    }

    if (response.status === 404) {
      // If the settings file was not found, we silently ignore the error. We
      // expect many installation to provide this file.
      console.debug(`'${settingsPath}' returned 404: ignoring`);
      return null;
    } else if (!response.ok) {
      console.error(
        `Fetching '${settingsPath}' failed: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    if (response.headers.get("Content-Type")?.startsWith("text/html")) {
      console.warn(`'${settingsPath}' request has 'Content-Type: text/html' -> ignoring...`);
      return null;
    }

    try {
      return parseToml(await response.text());
    } catch (e) {
      console.error(`Could not parse '${settingsPath}' as TOML: `, e);
      throw new SyntaxError(`Could not parse '${settingsPath}' as TOML: ${e}`);
    }
  }

  /**
   * Stores the given `newSettings` as user settings. The given object might be
   * partial, i.e. only the new values can be specified. Values in `newSettings`
   * override values in the old user settings.
   */
  saveSettings(newSettings: Settings) {
    this.#userSettings = merge(this.#userSettings, newSettings);
    const persistedQuality = this.#userSettings?.performance?.quality;
    if (persistedQuality !== undefined) {
      console.debug(`[settings] persisted performance.quality=${persistedQuality}`);
    }
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.#userSettings));
    this.onChange(this.settings());
  }

  /** The merged settings that the whole application should use. */
  settings(): Settings {
    const merged: Settings = mergeAll([this.#userSettings, this.contextSettings, this.urlSettings,
      { opencast: this.opencast, upload: this.upload }]) as Settings;

    const q = merged.performance?.quality;
    if (q && q !== "auto") {
      const overrides = mapQualityToOverrides(q);
      return merge(merged, overrides);
    }

    // TODO: is it ok that error conditions didn't make it in here?
    return merged;
  }

  /**
   * The values for the settings forms. These are simply the user settings with
   * missing settings filled by `defaultSettings`.
   */
  formValues() {
    return merge(defaultSettings, this.#userSettings);
  }

  fixedSettings(): Settings {
    return merge(this.contextSettings, this.urlSettings);
  }

  /**
   * Returns whether a specific setting is configurable by the user. It is not
   * if the setting is fixed by the context setting or an URL setting. The path
   * is given as string. Example: `manager.isConfigurable('opencast.loginName')`
   */
  isConfigurable(path: string) {
    let obj = this.fixedSettings();
    const segments = path.split(".");
    for (const segment of segments) {
      if (!(segment in obj)) {
        return true;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      obj = (obj as any)[segment];
    }

    return false;
  }

  isUsernameConfigurable() {
    return this.isConfigurable("opencast.loginName")
      && this.fixedSettings().opencast?.loginProvided !== true;
  }
  isPasswordConfigurable() {
    return this.isConfigurable("opencast.loginPassword")
      && this.fixedSettings().opencast?.loginProvided !== true;
  }
}



// ==============================================================================================
// ===== Setting runtime validation
// ==============================================================================================

/**
 * Validate the given `obj` with the global settings `SCHEMA`. If `allowParse`
 * is true, string values are attempted to parse into the expected type. `src`
 * must be one of `SRC_SERVER`, `SRC_URL` or `SRC_LOCAL_STORAGE`.
 * `srcDescription` is just a string for error messages specifying where `obj`
 * comes from.
 */
const validate = (
  obj: object,
  allowParse: boolean,
  src: SettingsSource,
  sourceDescription: string,
): Settings => {
  type SchemaOrValidator = Validator<unknown> | { [key: string]: SchemaOrValidator };

  // Validates `obj` with `schema`. `path` is the current path used for error
  // messages.
  const validate = (schema: SchemaOrValidator, obj: unknown, path: string) => {
    if (typeof schema === "function") {
      return validateValue(schema, obj, path);
    } else if (obj && typeof obj === "object") {
      return validateObj(schema, obj, path);
    } else {
      return unreachable();
    }
  };

  // Validate a settings value with a validation function. Returns the final
  // value of the setting or `null` if it should be ignored.
  const validateValue = (validation: Validator<unknown>, value: unknown, path: string) => {
    try {
      const newValue = validation(value, allowParse, src);
      return newValue === undefined ? value : newValue;
    } catch (error) {
      console.warn(
        `Validation of setting '${path}' (${sourceDescription}) failed -> ignoring...`,
        { value, error },
      );
      return null;
    }
  };

  // Validate a settings object/namespace. `schema` and `obj` need to be
  // objects.
  const validateObj = (schema: Record<string, SchemaOrValidator>, obj: object, path: string) => {
    // We iterate through all keys of the given settings object, checking if
    // each key is valid and recursively validating the value of that key.
    const out = Object.create(null);
    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key;
      if (key in schema && key in obj) {
        const validatedValue = validate(schema[key], value, newPath);

        // If `null` is returned, the validation failed and we ignore this
        // value.
        if (validatedValue !== null) {
          out[key] = validatedValue;
        }
      } else {
        console.warn(
          `'${newPath}' (${sourceDescription}) is not a valid settings key. Ignoring.`,
        );
      }
    }

    return out;
  };

  return validate(SCHEMA, obj, "");
};

/**
 * Validation function for a setting value.
 *
 * `v` is the input value and could be anything. `allowParse` specifies whether
 * the input might be parsed into the correct type (this is only `true` for GET
 * parameters). The validation should throw an error if the input value is not
 * valid for the setting. Otherwise the function must return a value which is
 * then used in the validated settings object. The returned value might be `v`
 * or something else.
 */
type Validator<T> = (v: unknown, allowParse: boolean, src: SettingsSource) => T;

/** Validation functions for basic types. */
const types = {
  "string": v => {
    if (typeof v !== "string") {
      throw new Error("is not a string, but should be");
    }
    return v;
  },
  "int": (v, allowParse): number => {
    if (Number.isInteger(v)) {
      return v as number;
    }

    if (allowParse && typeof v === "string") {
      if (/^[-+]?(\d+)$/.test(v)) {
        return Number(v);
      }

      throw new Error("can't be parsed as integer");
    } else {
      throw new Error("is not an integer");
    }
  },
  "boolean": (v, allowParse): boolean => {
    if (typeof v === "boolean") {
      return v;
    }

    if (allowParse) {
      if (v === "true") {
        return true;
      }
      if (v === "false") {
        return false;
      }
      throw new Error("can't be parsed as boolean");
    } else {
      throw new Error("is not a boolean");
    }
  },
  positiveInteger: (v, allowParse): number => {
    const i = types.int(v, allowParse);
    if (i <= 0) {
      throw new Error("has to be positive, but isn't");
    }

    return i;
  },
  "array": elementType => {
    return (v, allowParse, src) => {
      if (typeof v === "string" && allowParse) {
        try {
          v = JSON.parse(v);
        } catch {
          throw new Error("can't be parsed as array");
        }
      }

      if (!Array.isArray(v)) {
        throw new Error("is not an array");
      }

      return v.map(element => {
        try {
          return elementType(element, allowParse, src);
        } catch (err) {
          throw new Error(`failed to validate element '${element}' of array: ${err}`);
        }
      });
    };
  },
} satisfies {
  string: Validator<string>;
  int: Validator<number>;
  boolean: Validator<boolean>;
  positiveInteger: Validator<number>;
  array: <T, >(validator: Validator<T>) => Validator<T[]>;
};

/** Validator for `FormFieldState`. */
const metaDataField: Validator<FormFieldState> = v => {
  if (typeof v !== "string") {
    throw new Error("has to be a string");
  }

  if (![FORM_FIELD_HIDDEN, FORM_FIELD_OPTIONAL, FORM_FIELD_REQUIRED].includes(v)) {
    throw new Error(
      `has to be either '${FORM_FIELD_HIDDEN}', '${FORM_FIELD_OPTIONAL}' or `
        + `'${FORM_FIELD_REQUIRED}', but is '${v}'`,
    );
  }

  return v as FormFieldState;
};

/**
 * A validator wrapper that errors of the source of the value is NOT
 * `settings.toml`.
 */
const onlyFromServer = <Out, >(inner: Validator<Out>): Validator<Out> => (
  (v, allowParse, src) => {
    if (src !== "src-server") {
      throw new Error("this configuration cannot be specified via the URL or local storage, "
        + "but must be specified in 'settings.toml'");
    }

    return inner(v, allowParse, src);
  }
);

/** Defines all potential settings and their validation functions. */
const SCHEMA = {
  opencast: {
    serverUrl: v => {
      const s = types.string(v);

      if (s === "/" || s === "") {
        return;
      }

      const url = new URL(s);
      if (url.protocol !== "https:" && url.protocol !== "http:") {
        throw new Error('the URL does not start with "http:" or "https:"');
      }

      // TODO: we could return the `URL` here or do other adjustments
      return v;
    },
    serverType: v => {
      const s = types.string(v);
      if (s !== "MVP" && s !== "MOM") {
        throw new Error("has to be either 'MVP' or 'MOM'");
      }
      return s;
    },
    loginName: types.string,
    loginPassword: types.string,
    loginProvided: types.boolean,
    sfApiKey: types.string,
    token: types.string,
  },
  upload: {
    seriesId: types.string,
    workflowId: types.string,
    acl: (v, allowParse) => {
      if ((allowParse && v === "false") || v === false) {
        return false;
      }
      if ((allowParse && v === "true") || v === true) {
        return true;
      }

      if (typeof v === "string" && v.trim().startsWith("<")) {
        return v;
      }

      let obj = v;
      if (allowParse && typeof v === "string") {
        const json = decodeURIComponent(v);
        obj = JSON.parse(json);
      }

      if (typeof obj === "object" && obj) {
        const out = new Map<string, string[]>();
        for (const [key, value] of Object.entries(obj)) {
          if (!Array.isArray(value) || value.some(x => typeof x !== "string")) {
            throw new Error("values of ACL object need to be string arrays");
          }

          // "Useless" map to get rid of other properties inside array from toml parsing
          out.set(key, value.map(v => v));
        }

        return out;
      }

      throw new Error("needs to be 'true', 'false', an object or an XML string");
    },
    dcc: types.string,
    titleField: metaDataField,
    presenterField: metaDataField,
    seriesField: metaDataField,
    autofillPresenter: (v, allowParse, src) => {
      const a = types.array(v => {
        const s = types.string(v);
        if (!(PRESENTER_SOURCES as readonly string[]).includes(s)) {
          throw new Error("invalid presenter name source");
        }
        return s;
      })(v, allowParse, src);
      if (new Set(a).size < a.length) {
        throw new Error("duplicate presenter name source");
      }
      return a;
    },
    presentationId: types.string,
    uploadUrl: types.string,
    createMediaUploadUrl: types.string,
    uploadWhileRecording: types.boolean,
  },
  recording: {
    videoBitrate: types.positiveInteger,
    mimes: types.array(types.string),
    skipStopConfirmation: types.boolean,
  },
  review: {
    disableCutting: types.boolean,
  },
  teleprompter: {
    keepScriptAfterPublish: types.boolean,
  },
  display: {
    maxFps: types.positiveInteger,
    maxHeight: types.positiveInteger,
  },
  camera: {
    maxFps: types.positiveInteger,
    maxHeight: types.positiveInteger,
  },
  performance: {
    quality: v => {
      if (typeof v !== "string") {
        throw new Error("has to be a string");
      }
      const allowed = ["auto", "low", "medium", "high", "maximum"];
      if (!allowed.includes(v)) {
        throw new Error(`must be one of ${allowed.join(",")}`);
      }
      return v;
    },
  },
  return: {
    allowedDomains: onlyFromServer(types.array(types.string)),
    label: types.string,
    target: v => {
      if (typeof v !== "string") {
        throw new Error("has to be a string");
      }
      if (!(v.startsWith("/") || v.startsWith("http"))) {
        throw new Error("has to start with '/' or 'http'");
      }
      return v;
    },
  },
} satisfies Record<string, Record<string, Validator<unknown>>>;


// ==============================================================================================
// ===== Utilities
// ==============================================================================================

// Customize array merge behavior
/* const mergeOptions: deepmerge.Options = {
  arrayMerge: (_destinationArray, sourceArray, _options) => sourceArray,
  isMergeableObject: isPlainObject,
};*/
const merge = (a: Settings, b: Settings): Settings => deepmerge(a, b);
const mergeAll = (array: Settings[]) => deepmerge(...array);

/**
 * Junior dev note:
 * These are temporary runtime caps we apply from the quality preset.
 * We are not changing the user's raw camera/display preferences here,
 * just providing sane limits so blur and capture stay smooth per device class.
 */
export const mapQualityToOverrides = (q: NonNullable<Settings["performance"]>["quality"]): Settings => {
  switch (q) {
    case "low":
      return {
        display: { maxHeight: 720 },
        camera: { maxHeight: 480, maxFps: 30 },
      };
    case "medium":
      return {
        display: { maxHeight: 1080 },
        camera: { maxHeight: 720, maxFps: 30 },
      };
    case "high":
      return {
        display: { maxHeight: 1080 },
        camera: { maxHeight: 1080 },
      };
    default:
      return {};
  }
};

/**
 * Detect a reasonable default quality for this device.
 * Returns one of "low" | "medium" | "high".
 *
 * Junior dev note:
 * This is intentionally heuristic-based, not exact benchmarking.
 * We look at coarse signals (mobile, cores, memory, GPU info) to pick
 * a starting point quickly at app startup, then users can override it.
 */
export const detectQualityByDevice = (): NonNullable<Settings["performance"]>["quality"] => {
  try {
    if (onMobileDevice()) {
      return "medium"; // Mobile defaults to conservative Medium (UI may force Low)
    }

    const gpu = getWebGLRendererInfo();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deviceMemory = (navigator as any).deviceMemory ?? 0;
    const hwCores = navigator.hardwareConcurrency ?? 0;
    // user agent string not needed for detection currently

    // If there's no GPU info or it's a software rasterizer, treat as Low.
    if (!gpu || gpu.softwareRasterizer) {
      return "low";
    }

    const renderer = (gpu.renderer || "").toLowerCase();
    const vendor = (gpu.vendor || "").toLowerCase();
    const combined = `${vendor} ${renderer}`;

    // Apple Silicon / Apple GPU detection
    if (/apple/i.test(combined) || /m1|m2|apple gpu/i.test(combined) || /apple/i.test(navigator.platform)) {
      return "high";
    }

    // High-end discrete GPUs (NVIDIA/AMD) or modern Adreno/Mali with enough resources
    const hasDiscrete = /nvidia|geforce|radeon|amd|adreno|mali|qualcomm/i.test(combined);
    if (hasDiscrete && deviceMemory >= 8 && hwCores >= 8) {
      return "high";
    }

    // Low memory or few cores -> Low
    if (hwCores > 0 && hwCores <= 4) {
      return "low";
    }
    if (deviceMemory > 0 && deviceMemory <= 4) {
      return "low";
    }

    // If none of the above, pick Medium as a conservative default.
    return "medium";
  } catch (e) {
    console.warn("Quality detection failed, falling back to medium", e);
    return "medium";
  }
};

// ==============================================================================================
// ===== React context for settings
// ==============================================================================================

const Context = React.createContext<Settings | null>(null);
const ManagerContext = React.createContext<SettingsManager | null>(null);

/** Returns the current settings. */
export const useSettings = (): Settings => usePresentContext(Context, "useSettings");

/** Returns the settings manager. */
export const useSettingsManager = (): SettingsManager =>
  usePresentContext(ManagerContext, "useSettingsManager");

type ProviderProps = React.PropsWithChildren<{
  settingsManager: SettingsManager;
}>;

export const Provider: React.FC<ProviderProps> = ({ settingsManager, children }) => {
  const [settings, updateSettings] = useState<Settings>(settingsManager.settings());
  settingsManager.onChange = (newSettings: Settings) => updateSettings(newSettings);

  // This debug output will be useful for future debugging sessions.
  useEffect(() => {
    console.debug("Current settings: ", settings);
  });

  return (
    <ManagerContext.Provider value={settingsManager}>
      <Context.Provider value={settings}>
        {children}
      </Context.Provider>
    </ManagerContext.Provider>
  );
};
